import type { EntityData } from '@adornis/baseql/entities/types.js';
import type { FunctionalForm } from '@adornis/forms/use-form.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { openPromptStringFieldDialog } from '@martinus/styles/client/dialogs/PromptStringFieldDialog.js';
import { html, nothing } from 'lit';
import { Permission } from '../../../../permissions/roles.js';
import type { Person } from '../../../entities/person.js';

export const EmployeeSectionInternalIdentifiers = stateful((props: { form: FunctionalForm<EntityData<Person>> }) => {
  const user = useObservable(() => currentUser(), []);
  if (!user) return nothing;
  return html`
    <div class="card card-body flex-row! flex-wrap">
      <div class="flex flex-row items-center gap-2">
        <span class="text-sm font-semibold text-base-content/70">Personalnummer</span>
        <div class="badge badge-lg badge-outline">
          <p>${props.form.document.mitarbeiterDaten?.personalNr ?? '---'}</p>

          ${
            user.hasPermission(Permission.AN_SEKTION_W)
              ? html`
                  <button
                    class="btn btn-ghost btn-circle btn-dash btn-xs -mr-[13px]"
                    @click=${async () => {
                      if (!props.form.document.mitarbeiterDaten) return;

                      const { value } = await openPromptStringFieldDialog(
                        props.form.document.mitarbeiterDaten.personalNr,
                        {
                          title: 'Personalnummer ändern',
                          placeholder: 'Personalnummer',
                          description: 'Hier können Sie die Personalnummer für den Benutzer verwalten.',
                        },
                      );
                      if (value) {
                        props.form.document.mitarbeiterDaten.personalNr = value;
                      }
                    }}
                  >
                    <span class="icon-[material-symbols--edit] text-lg"></span>
                  </button>
                `
              : nothing
          }
        </div>
      </div>

      <div class="flex flex-row items-center gap-2">
        <span class="text-sm font-semibold text-base-content/70">Ehemalige Personalnummer</span>
        <div class="badge badge-lg badge-outline">
          <p>${props.form.document.mitarbeiterDaten?.altePersonalNr ?? '---'}</p>

          ${
            user.hasPermission(Permission.AN_SEKTION_W)
              ? html`
                  <button
                    class="btn btn-ghost btn-circle btn-dash btn-xs -mr-[13px]"
                    @click=${async () => {
                      if (!props.form.document.mitarbeiterDaten) return;

                      const { value } = await openPromptStringFieldDialog(
                        props.form.document.mitarbeiterDaten.altePersonalNr,
                        {
                          title: 'Ehemalige Personalnummer ändern',
                          placeholder: 'Ehemalige Personalnummer',
                          description:
                            'Sollten Mitarbeitende bereits früher in diesem oder anderen Systemen geführt worden sein, können ehemalige Personalnummern bestehen bleiben. Diese müssen hier eingetragen werden um die einzigartige Zuordnung zu gewährleisten.',
                        },
                      );
                      if (value) {
                        props.form.document.mitarbeiterDaten.altePersonalNr = value;
                      }
                    }}
                  >
                    <span class="icon-[material-symbols--edit] text-lg"></span>
                  </button>
                `
              : nothing
          }
        </div>
      </div>

      <div class="flex flex-row items-center gap-2">
        <span class="text-sm font-semibold text-base-content/70">Lodasnummer</span>
        <div class="badge badge-lg badge-outline">
          <p>${props.form.document.mitarbeiterDaten?.lodasPersonalNr ?? '---'}</p>

          ${
            user.hasPermission(Permission.AN_SEKTION_W)
              ? html`
                  <button
                    class="btn btn-ghost btn-circle btn-dash btn-xs -mr-[13px]"
                    @click=${async () => {
                      if (!props.form.document.mitarbeiterDaten) return;

                      const { value } = await openPromptStringFieldDialog(
                        props.form.document.mitarbeiterDaten.lodasPersonalNr,
                        {
                          title: 'Lodasnummer ändern',
                          placeholder: 'Lodasnummer',
                          description:
                            'Das Lohndaten-System (Lodas) in DATEV verwendet eigene Personalnummern für Mitarbeitende. Diese werden vom System selbst vergeben und können hier zur Korrektur angepasst werden.',
                        },
                      );
                      if (value) {
                        props.form.document.mitarbeiterDaten.lodasPersonalNr = value;
                      }
                    }}
                  >
                    <span class="icon-[material-symbols--edit] text-lg"></span>
                  </button>
                `
              : nothing
          }
        </div>
      </div>
    </div>
  `;
});
